// Merged post + video feed. Feed length is no longer bounded by post count:
// videos interleave while posts last, then the remaining video budget appends,
// so the feed stays full even when the community is quiet.
//
// Videos are spread across the WHOLE feed rather than a fixed every-N-posts
// stride. A fixed stride burned the entire video budget in the first
// (videoCap * postsPerVideo) posts, so everything past that point was pure
// posts — and the dry tail grew as the community got healthier (50 posts =>
// 54% of the feed video-free, 80 posts => 70%). Instead the top keeps its
// dense cadence (that's where engagement is) and the remaining budget
// stretches over the tail, so coverage self-corrects as post volume grows.

export function buildFeed(posts, videos, opts = {}) {
  const { postsPerVideo = 2, videoCap = 9, denseVideos = 4 } = opts;

  const budget = Math.min(videoCap, videos.length);
  if (budget === 0) return posts.map((post) => ({ type: 'post', item: post }));

  // Post indices to insert a video AFTER. Dense phase keeps the original
  // every-postsPerVideo cadence at the top; the rest spread over the tail.
  const dense = Math.min(denseVideos, budget);
  const slots = [];
  for (let k = 1; k <= dense; k++) slots.push(k * postsPerVideo - 1);

  const rest = budget - dense;
  const tailStart = dense * postsPerVideo;
  const tailPosts = posts.length - tailStart;
  if (rest > 0 && tailPosts > 0) {
    const stride = tailPosts / rest;
    for (let k = 1; k <= rest; k++) {
      slots.push(tailStart + Math.round(k * stride) - 1);
    }
  }

  // Rounding can collide when stride < 1; one video per post boundary.
  const slotFor = new Map();
  let videoIndex = 0;
  for (const slot of slots) {
    if (videoIndex >= budget) break;
    if (slot < 0 || slot >= posts.length || slotFor.has(slot)) continue;
    slotFor.set(slot, videoIndex);
    videoIndex++;
  }

  const items = [];
  posts.forEach((post, i) => {
    items.push({ type: 'post', item: post });
    if (slotFor.has(i)) items.push({ type: 'video', item: videos[slotFor.get(i)] });
  });

  // Quiet community: posts ran out before the budget did, so append the rest.
  while (videoIndex < budget) {
    items.push({ type: 'video', item: videos[videoIndex] });
    videoIndex++;
  }

  return items;
}
